import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ResizableSplitProps {
  /** Content for the left pane (e.g. the document editor). */
  left: ReactNode;
  /** Content for the right pane (e.g. the Q&A index panel). */
  right: ReactNode;
  /** Initial width of the left pane as a percentage of the container (0–100). */
  defaultSplit?: number;
  /** Minimum width of either pane, as a percentage. */
  minPercent?: number;
  /** Minimum width of either pane, in pixels — whichever is larger wins. */
  minPx?: number;
  /** Percentage the keyboard arrows nudge the split by. */
  step?: number;
  /** Accessible label for the drag handle. */
  ariaLabel?: string;
  className?: string;
}

/**
 * A hand-rolled two-pane horizontal resizer: `left` | draggable divider |
 * `right`. The split is tracked as the left pane's width percentage; the right
 * pane fills the remainder. Dragging the divider (pointer events, so touch and
 * mouse both work) updates the split from the cursor's x position relative to
 * the container; Left/Right arrows nudge it for keyboard users. Neither pane is
 * allowed to collapse below `minPercent`/`minPx`.
 *
 * Below the `lg` breakpoint the panes stack vertically and the divider hides,
 * preserving the prior responsive behavior.
 */
export function ResizableSplit({
  left,
  right,
  defaultSplit = 55,
  minPercent = 20,
  minPx = 280,
  step = 2,
  ariaLabel = 'Resize editor and panel',
  className,
}: ResizableSplitProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [split, setSplit] = useState(defaultSplit);

  /** Clamp a candidate percentage so neither pane drops below the minimums. */
  const clamp = useCallback(
    (percent: number): number => {
      const width = containerRef.current?.getBoundingClientRect().width ?? 0;
      const pxFloor = width > 0 ? (minPx / width) * 100 : 0;
      const min = Math.max(minPercent, pxFloor);
      const max = 100 - min;
      if (max < min) return 50;
      return Math.min(max, Math.max(min, percent));
    },
    [minPercent, minPx],
  );

  const updateFromClientX = useCallback(
    (clientX: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      const percent = ((clientX - rect.left) / rect.width) * 100;
      setSplit(clamp(percent));
    },
    [clamp],
  );

  const onPointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
      updateFromClientX(e.clientX);
    },
    [updateFromClientX],
  );

  const onPointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setSplit((s) => clamp(s - step));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setSplit((s) => clamp(s + step));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setSplit(clamp(minPercent));
      } else if (e.key === 'End') {
        e.preventDefault();
        setSplit(clamp(100 - minPercent));
      }
    },
    [clamp, minPercent, step],
  );

  const valueNow = Math.round(split);

  return (
    <div
      ref={containerRef}
      className={cn('flex h-full flex-col gap-4 lg:flex-row lg:gap-0', className)}
    >
      {/* Left pane: fixed-width on lg+, full-width when stacked. `overflow-hidden`
          clips the pane so its content scrolls internally, not the page. */}
      <div
        className="min-h-[300px] min-w-0 flex-1 overflow-hidden lg:min-h-0 lg:flex-none"
        style={{ flexBasis: `${split}%` }}
      >
        {left}
      </div>

      {/* Divider: a focusable separator. Hidden (stacked layout) below lg. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={valueNow}
        aria-valuemin={Math.round(minPercent)}
        aria-valuemax={Math.round(100 - minPercent)}
        aria-label={ariaLabel}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={onKeyDown}
        className={cn(
          'group hidden shrink-0 touch-none select-none items-center justify-center',
          'lg:flex lg:w-3 lg:cursor-col-resize',
          'focus-visible:outline-none',
        )}
      >
        {/* The visible grab line; widens/colors on hover, focus, and active. */}
        <div
          aria-hidden="true"
          className={cn(
            'h-full w-px rounded-full bg-border transition-colors',
            'group-hover:w-1 group-hover:bg-primary/60',
            'group-focus-visible:w-1 group-focus-visible:bg-primary',
            'group-active:bg-primary',
          )}
        />
      </div>

      {/* Right pane fills the remainder; clips so the Q&A list scrolls internally. */}
      <div className="min-h-[300px] min-w-0 flex-1 overflow-hidden lg:min-h-0">{right}</div>
    </div>
  );
}
